import type React from 'react';
import { useEffect, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { wrappedPromise } from 'app/client/google/utils';
import { css } from '@/styled-system/css';

interface PointShape {
  x: number;
  y: number;
}

interface SizeShape {
  width: number;
  height: number;
}

interface LatLngShape {
  lat: number;
  lng: number;
}

interface IconProps {
  url: string;
  anchor?: PointShape;
  labelOrigin?: PointShape;
  origin?: PointShape;
  scaledSize?: SizeShape;
  size?: SizeShape;
}

interface MarkerProps {
  enableDrag?: boolean;
  icon: IconProps;
  infoWindowContent?: React.ReactNode;
  map?: Window['google']['maps']['Map'] | null;
  name?: string;
  position: LatLngShape;
  verticalOffset?: number;
}

const infoWindowContentStyle = css({
  paddingTop: '1x',
  paddingRight: '1x',
  paddingBottom: '1x',
  paddingLeft: '0',
});

const createGoogleSize = (size: SizeShape): Window['google']['maps']['Size'] =>
  new window.google.maps.Size(size.width, size.height);
const createGooglePoint = (point: PointShape): Window['google']['maps']['Point'] =>
  new window.google.maps.Point(point.x, point.y);

export const Marker: React.FC<MarkerProps> = ({
  map,
  position,
  icon,
  infoWindowContent,
  enableDrag = false,
  verticalOffset = -5,
}) => {
  const markerRef = useRef<Window['google']['maps']['Marker'] | null>(null);
  const infoWindowRef = useRef<Window['google']['maps']['InfoWindow'] | null>(null);
  const infoWindowRootRef = useRef<Root | null>(null);
  const markerPromiseRef = useRef<{
    promise: Promise<Window['google']['maps']['Marker']>;
    resolve: (value: Window['google']['maps']['Marker']) => void;
  }>({
    promise: new Promise<Window['google']['maps']['Marker']>(() => {}),
    resolve: () => {},
  });

  useEffect(() => {
    if (!window.google || !window.google.maps || !map) {
      if (markerRef.current) {
        if (infoWindowRef.current) {
          infoWindowRootRef.current?.unmount();
          infoWindowRef.current.close();
          infoWindowRef.current = null;
          infoWindowRootRef.current = null;
        }
        markerRef.current.setMap(null);
        markerRef.current = null;
        markerPromiseRef.current = wrappedPromise();
      }
      return;
    }

    if (markerRef.current) {
      if (infoWindowRef.current) {
        infoWindowRootRef.current?.unmount();
        infoWindowRef.current.close();
        infoWindowRef.current = null;
        infoWindowRootRef.current = null;
      }
      markerRef.current.setMap(null);
      markerRef.current = null;
      markerPromiseRef.current = wrappedPromise();
    }

    const { Size, Marker: GoogleMarker, InfoWindow } = window.google.maps;

    const googleIcon: Window['google']['maps']['Icon'] | Window['google']['maps']['Symbol'] | string = {
      url: icon.url,
      ...(icon.size && { size: createGoogleSize(icon.size) }),
      ...(icon.scaledSize && { scaledSize: createGoogleSize(icon.scaledSize) }),
      ...(icon.anchor && { anchor: createGooglePoint(icon.anchor) }),
      ...(icon.labelOrigin && { labelOrigin: createGooglePoint(icon.labelOrigin) }),
      ...(icon.origin && { origin: createGooglePoint(icon.origin) }),
    };

    const newMarker = new GoogleMarker({
      map,
      position,
      icon: googleIcon,
      draggable: enableDrag,
    });

    markerRef.current = newMarker;

    if (infoWindowContent) {
      const contentDiv = document.createElement('div');
      contentDiv.className = infoWindowContentStyle;

      const root = createRoot(contentDiv);
      root.render(<>{infoWindowContent}</>);
      infoWindowRootRef.current = root;

      const newInfoWindow = new InfoWindow({
        content: contentDiv,
        disableAutoPan: true,
        pixelOffset: new Size(0, verticalOffset),
      });

      newInfoWindow.open({
        anchor: newMarker,
        map,
        shouldFocus: false,
      });
      newInfoWindow.setZIndex(999);

      infoWindowRef.current = newInfoWindow;
    }

    markerPromiseRef.current.resolve(newMarker);

    return () => {
      if (infoWindowRootRef.current) {
        infoWindowRootRef.current.unmount();
        infoWindowRootRef.current = null;
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, [map, position, icon, infoWindowContent, enableDrag, verticalOffset]);

  return null;
};

Marker.displayName = 'Marker';

export default Marker;
